import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../lib/api";
import { AdminNav } from "../components/AdminNav";
import { Loader2, Eye, EyeOff, Save, Check } from "lucide-react";

const LS_TOKEN = "bnm-admin-token";
const usd = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);

export default function AdminOfferings() {
  const [token] = useState(() => localStorage.getItem(LS_TOKEN) || "");
  const [err, setErr] = useState("");
  const [products, setProducts] = useState([]);
  const [plates, setPlates] = useState([]);
  const [editing, setEditing] = useState(null); // plate index being edited
  const [draft, setDraft] = useState({ title: "", caption: "" });
  const [savedIdx, setSavedIdx] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const [p, g] = await Promise.all([
        axios.get(`${API}/admin/products`, auth),
        axios.get(`${API}/admin/gallery`, auth),
      ]);
      setProducts(p.data.products || []);
      setPlates(g.data.items || []);
    } catch (e) { setErr(e.response?.status === 401 ? "Please sign in at /admin first." : "Could not load offerings."); }
  }, [token]);

  useEffect(() => { if (token) load(); else setErr("Please sign in at /admin first."); }, [token, load]);

  const [toast, setToast] = useState("");
  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 1800); };

  const toggleProduct = async (slug, isActive) => {
    try { await axios.patch(`${API}/admin/products/${slug}`, { is_active: !isActive }, auth); setProducts((ps) => ps.map((x) => (x.slug === slug ? { ...x, is_active: !isActive } : x))); flash(isActive ? "Product hidden" : "Product visible"); }
    catch (e) { flash("Could not update product"); }
  };

  const startEdit = (p) => { setEditing(p.index); setDraft({ title: p.title, caption: p.caption }); };
  const savePlate = async (index) => {
    try {
      await axios.patch(`${API}/admin/gallery/${index}`, draft, auth);
      setPlates((ps) => ps.map((x) => (x.index === index ? { ...x, ...draft } : x)));
      setEditing(null); setSavedIdx(index); setTimeout(() => setSavedIdx(null), 1600); flash("Saved");
    } catch (e) { flash("Could not save plate"); }
  };
  const toggleHidden = async (index, hidden) => {
    try { await axios.patch(`${API}/admin/gallery/${index}`, { is_hidden: !hidden }, auth); setPlates((ps) => ps.map((x) => (x.index === index ? { ...x, is_hidden: !hidden } : x))); flash(hidden ? "Plate shown" : "Plate hidden"); }
    catch (e) { flash("Could not update plate"); }
  };

  if (err) return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-ivory-100 px-5 text-center">
      <div><p className="text-ink-soft" data-testid="offerings-admin-error">{err}</p><Link to="/admin" className="mt-3 inline-block text-gold-deep underline">Go to /admin</Link></div>
    </div>
  );

  return (
    <div className="min-h-[100dvh]" style={{ background: "linear-gradient(180deg,#F3EFEA 0%,#ECE7DE 100%)" }} data-testid="admin-offerings">
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <AdminNav />
        <h1 className="font-display text-3xl text-ink">Offerings & Gallery</h1>
        {toast && <p className="mt-2 text-sm text-sage-deep" data-testid="offerings-toast">{toast}</p>}

        {/* Products */}
        <section className="mt-6">
          <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">Store products</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {products.map((p) => (
              <div key={p.slug} className="flex items-center justify-between gap-3 rounded-2xl bg-ivory-50 p-4 shadow-sm ring-1 ring-ivory-400/60" data-testid={`product-row-${p.slug}`}>
                <div className="min-w-0">
                  <p className="truncate font-display text-ink">{p.title}</p>
                  <p className="text-xs text-ink-muted">Door {p.door_number} · {p.is_recurring ? `${usd(p.standard_price_cents || p.price_cents)}/mo` : usd(p.price_cents)}</p>
                </div>
                <button type="button" onClick={() => toggleProduct(p.slug, p.is_active)} data-testid={`product-toggle-${p.slug}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-display ${p.is_active ? "bg-sage/20 text-sage-deep" : "bg-ivory-200 text-ink-muted"}`}>
                  {p.is_active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {p.is_active ? "Visible" : "Hidden"}
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Gallery plates */}
        <section className="mt-10">
          <p className="text-[11px] uppercase tracking-[0.14em] text-ink-muted">Art gallery plates</p>
          <div className="mt-3 grid gap-3">
            {plates.map((p) => (
              <div key={p.index} className="flex gap-4 rounded-2xl bg-ivory-50 p-4 shadow-sm ring-1 ring-ivory-400/60" data-testid={`plate-row-${p.index}`}>
                <img src={p.image} alt="" className="h-20 w-32 flex-none rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  {editing === p.index ? (
                    <>
                      <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid={`plate-title-${p.index}`}
                        className="w-full rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2 text-sm font-display text-ink" />
                      <textarea value={draft.caption} onChange={(e) => setDraft({ ...draft, caption: e.target.value })} rows={2} data-testid={`plate-caption-${p.index}`}
                        className="mt-2 w-full resize-y rounded-lg border border-ivory-400/70 bg-[#FCFBF7] p-2 text-sm text-ink" />
                      <div className="mt-2 flex gap-2">
                        <button type="button" onClick={() => savePlate(p.index)} data-testid={`plate-save-${p.index}`} className="inline-flex items-center gap-1 rounded-full bg-ink px-3 py-1.5 text-xs text-ivory-50"><Save className="h-3.5 w-3.5" /> Save</button>
                        <button type="button" onClick={() => setEditing(null)} className="rounded-full px-3 py-1.5 text-xs text-ink-soft">Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="font-display text-ink">{p.title} {savedIdx === p.index && <Check className="ml-1 inline h-4 w-4 text-sage-deep" />}</p>
                      <p className="mt-0.5 text-sm text-ink-soft line-clamp-2">{p.caption}</p>
                      <div className="mt-2 flex gap-2">
                        <button type="button" onClick={() => startEdit(p)} data-testid={`plate-edit-${p.index}`} className="rounded-full bg-ivory-200 px-3 py-1.5 text-xs text-ink hover:bg-gold/10">Edit words</button>
                        <button type="button" onClick={() => toggleHidden(p.index, p.is_hidden)} data-testid={`plate-hide-${p.index}`}
                          className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs ${p.is_hidden ? "bg-ivory-200 text-ink-muted" : "bg-sage/20 text-sage-deep"}`}>
                          {p.is_hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {p.is_hidden ? "Hidden" : "Shown"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
